import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Icon } from 'semantic-ui-react';
import styled from 'styled-components';
import { BodyText, H2, PageSection, StyledCard, Subtitle } from 'common/styles/page';
import { ResourceCategoryRead, useGetResourceCategoriesQuery } from 'areas/settings/repository-management/api/repositoryApi';
import { MobilePageContent } from '@/mobile/layouts/common/MobilePageStyles';
import { CardsGrid, SectionDescription, SectionHeader, Title, MobileSpinner } from '@/mobile/layouts/common/MobilePageStyles';

const MobileKnowledgeBasePage = () => {
    const navigate = useNavigate();
    const [searchValue, setSearchValue] = useState('');

    const { data: categoriesData, isLoading: isCategoriesLoading } = useGetResourceCategoriesQuery(undefined, {
        refetchOnMountOrArgChange: true,
        refetchOnFocus: true,
    });
    const rawCategoriesData = categoriesData as unknown;
    const categories: ResourceCategoryRead[] = Array.isArray(rawCategoriesData)
        ? (rawCategoriesData as ResourceCategoryRead[])
        : Array.isArray((rawCategoriesData as { results?: ResourceCategoryRead[] })?.results)
            ? (rawCategoriesData as { results: ResourceCategoryRead[] }).results
            : [];

    const categoryCounts = categories.reduce((acc, category) => {
        const countsCategory = category as ResourceCategoryRead & {
            docs_count?: number;
            videos_count?: number;
            links_count?: number;
        };

        acc[category.id] = {
            docs: countsCategory.docs_count ?? 0,
            videos: countsCategory.videos_count ?? 0,
            links: countsCategory.links_count ?? 0,
        };
        return acc;
    }, {} as Record<number, { docs: number; videos: number; links: number }>);

    const groupLabels: Record<number, string> = {
        1: 'People & Culture',
        2: 'Leadership & Growth',
        3: 'Equity & Representation',
        4: 'Workplace Systems',
        5: 'Wellbeing & Human Skills',
        6: 'Impact & Innovation',
    };

    const cards = categories.map(category => ({
        title: category.name,
        description: category.description || 'Explore related resources in this category.',
        url: `/app/mobile/knowledge-base/resources?category=${category.slug}`,
        icon: category.icon,
        color: category.color,
        group: category.category_group ?? 0,
        counts: categoryCounts[category.id] || { docs: 0, videos: 0, links: 0 },
    }));

    const filteredCards = cards.filter(card => {
        const query = searchValue.trim().toLowerCase();
        if (!query) return true;
        return (
            (card.title ?? '').toLowerCase().includes(query) ||
            (card.description ?? '').toLowerCase().includes(query)
        );
    });

    const groupedCards = filteredCards.reduce((acc, card) => {
        const groupKey = card.group || 0;
        if (!acc[groupKey]) {
            acc[groupKey] = [];
        }
        acc[groupKey].push(card);
        return acc;
    }, {} as Record<number, typeof cards>);

    const sortedGroupKeys = Object.keys(groupedCards)
        .map(key => Number(key))
        .sort((a, b) => a - b);

    const handleCardClick = (url: string) => {
        navigate(url);
    };

    return (
        <MobilePageContent>
            <PageSection>
                <SectionHeader>
                    <Title>Knowledge Base</Title>
                </SectionHeader>
                <SectionDescription>
                    Explore curated resources for teams, leadership, and organizational performance.
                </SectionDescription>
                    <SearchSection>
                        <SearchInput
                            id="knowledge-base-search"
                            type="search"
                            placeholder="Search Knowledge Base"
                            value={searchValue}
                            onChange={event => setSearchValue(event.target.value)}
                            aria-label="Search Knowledge Base"
                            spellCheck={false}
                        />
                    </SearchSection>
            </PageSection>
            <PageSection>
                {isCategoriesLoading ? (
                    <MobileSpinner />
                ) : cards.length === 0 ? (
                    <div>No categories available.</div>
                ) : (
                    <div>
                        {sortedGroupKeys.map(groupKey => (
                            <div key={groupKey}>
                                <PageSection padtop>
                                    <Subtitle>{groupLabels[groupKey] || 'Other'}</Subtitle>
                                    <CardsGrid>
                                        {groupedCards[groupKey].map(card => (
                                            <KnowledgeCard
                                                key={card.title}
                                                onClick={() => handleCardClick(card.url)}
                                            >
                                                <CardHeader>
                                                    <CardIcon
                                                        style={{
                                                            background: card.color || 'rgba(95, 112, 255, 0.1)',
                                                            color: card.color ? '#fff' : '#5f70ff',
                                                        }}
                                                    >
                                                        {card.icon ? (
                                                            <Icon name={card.icon as any} />
                                                        ) : (
                                                            <Icon name="book" />
                                                        )}
                                                    </CardIcon>
                                                    <CardHeaderContent>
                                                        <H2>{card.title}</H2>
                                                        <CountsText>
                                                            {`${card.counts.docs} docs · ${card.counts.videos} videos · ${card.counts.links} links`}
                                                        </CountsText>
                                                    </CardHeaderContent>
                                                </CardHeader>
                                                <BodyText>{card.description}</BodyText>
                                            </KnowledgeCard>
                                        ))}
                                    </CardsGrid>
                                </PageSection>
                            </div>
                        ))}
                    </div>
                )}
            </PageSection>
        </MobilePageContent>
    );
};


const SearchSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: 100%;
  max-width: 720px;

  @media (max-width: 520px) {
    max-width: 100%;
    width: 100%;
    padding-right: 12px;
  }
`;

const SearchInput = styled.input`
  width: 400px;
  max-width: 100%;
  padding: 14px 18px;
  border-radius: 5px;
  border: 1px solid #d9d9d9;
  background-color: #fff;
  font-size: 14px;
  color: #111;
  transition: border-color 0.2s ease;
  box-sizing: border-box;

  &:focus {
    outline: none;
    border-color: #5f7fff;
    box-shadow: 0 0 0 3px rgba(95, 127, 255, 0.16);
  }

  @media (max-width: 520px) {
    width: 100%;
  }
`;


const KnowledgeCard = styled(StyledCard)`
  padding: 18px;
  min-height: 120px;
  display: flex;
  flex-direction: column;
  gap: 10px;
  cursor: pointer;
  border: 1px solid rgba(0, 0, 0, 0.08);
  transition: transform 0.2s ease, border-color 0.2s ease, box-shadow 0.2s ease;
`;



const CardHeader = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
`;

const CardHeaderContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const CountsText = styled.span`
  font-size: 0.80rem;
  color: rgba(34, 34, 34, 0.72);
  line-height: 1.0;
`;

const CardIcon = styled.div`
  width: 40px;
  height: 40px;
  display: grid;
  place-items: center;
  border-radius: 12px;
  background: rgba(95, 112, 255, 0.1);
  color: #5f70ff;
  font-size: 1.1rem;

  svg,
  i {
    position: relative;
    top: -3px;
    left: 2px;
  }
`;

export default MobileKnowledgeBasePage;
